/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef, inject } from '@angular/core';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { ControlOn, AppFormControl } from '@src/app/shared/form/app-form-control';
import { Field } from '@src/app/shared/field/Field';
import { LimitToPipe } from '@src/app/shared/pipes/limit-to.pipe';
import { NgClass } from '@angular/common';
import { FormErrorComponent } from '@src/app/shared/form/form-error/form-error.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';

/** One standard form-field slot, including the gap to the next field. */
const SLOT_PX = 74;
/** Label + mat-error + internal padding around the editable area. */
const CHROME_PX = 50;

export class TextAreaField extends Field {
    /**
     * How many standard field-slots tall the control is. 2 (the default) keeps the historic
     * 148px / 98px sizing - a textarea in a two-column layout then lines its following field up
     * with the third field of the other column. 4 == "double height", following field aligns
     * with the fifth.
     */
    heightInFields?: number;
}

@Component({
    selector: 'app-form-text-area',
    templateUrl: './form-text-area.component.html',
    imports: [
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        FormErrorComponent,
        NgClass,
        LimitToPipe,
    ]
})
export class FormTextAreaComponent implements FormControlInterface {

  @Input() control: AppFormControl;
  @Input() on: ControlOn = 'form';

  private confirmDialog = inject(ConfirmDialogService);

  showFullText() {
    if (this.control.value) {
      this.confirmDialog.showLongText(this.control.field.label, this.control.value);
    }
  }

  get formFieldHeightPx(): number {
    const slots = (this.control?.field as TextAreaField)?.heightInFields ?? 2;
    return slots * SLOT_PX;
  }

  get textAreaHeightPx(): number {
    return this.formFieldHeightPx - CHROME_PX;
  }

  public static make(label: string, value: string, options: Partial<TextAreaField> = {}) {
    const field = new TextAreaField({ label, value, type: 'longText' }, options);
    field.formControlFactory = FormTextAreaComponent.createComponent;
    return field;
  }

  public static createComponent(vcr: ViewContainerRef, ctl: AppFormControl, on: ControlOn) {
    const componentRef = vcr.createComponent(FormTextAreaComponent);
    componentRef.instance.control = ctl;
    componentRef.instance.on = on;
    return componentRef;
  }
}
